import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { readableTextColor, labelFontSize, labelFontWeight, resolvedSegmentPercentage, wrapLabel } from "../lib/chart_colors.js"

// Card #4273: a horizontal bar's category (y) axis had no wrap/truncation
// handling, so Chart.js clipped long category labels at the canvas edge
// (clipped from the label's start, since the category axis anchors text
// against the plot-area boundary and extends it outward with nowhere to go).
// wrapLabel() lives in chart_colors.js (round 2) so the other
// distribution/bar renderers can share it instead of each carrying their own
// copy.

// Renders a bar or column chart from pre-computed distribution data.
// 'bar' orientation = horizontal, 'column' orientation = vertical.
//
// Example:
//   <canvas data-controller="bar-chart"
//           data-bar-chart-labels-value='["Option A","Option B"]'
//           data-bar-chart-data-value='[12, 8]'
//           data-bar-chart-primary-color-value="#003d68"
//           data-bar-chart-orientation-value="vertical"
//           data-bar-chart-label-style-value="percentage">
//   </canvas>

export default class extends Controller {
  static values = {
    labels:       { type: Array,  default: [] },
    data:         { type: Array,  default: [] },
    primaryColor: { type: String, default: "#003d68" },
    orientation:  { type: String, default: "vertical" },
    labelStyle:   { type: String, default: "percentage" },
    colors:       { type: Array,  default: [] },
    denominator:  { type: Number, default: 0 },
    labelColor:   { type: String, default: "#003d68" },
    labelSize:    { type: String, default: "medium" },
    labelWeight:  { type: String, default: "bold" },
    progress:     { type: Boolean, default: false }
  }

  connect() {
    this.initializeChart()
  }

  // Card #4273: a Turbo Stream re-render can leave the stale <canvas> in
  // place while this controller reconnects, stacking a second Chart.js
  // instance on top of the first (same defect fixed in
  // doughnut_chart_controller.js and gauge_chart_controller.js).
  disconnect() {
    this.destroyChart()
  }

  destroyChart() {
    this.chart?.destroy()
    this.chart = null
  }

  initializeChart() {
    this.destroyChart()

    const ctx = this.element.getContext('2d')
    const horizontal = this.orientationValue === 'horizontal'
    const labels = this.labelsValue
    const data = this.dataValue
    const labelStyle = this.labelStyleValue
    const denominator = this.denominatorValue
    const progress = this.progressValue
    const labelColor = this.labelColorValue
    const maxValue = Math.max(...data, 0)
    const barColors = this.colorsValue.length === data.length
      ? this.colorsValue
      : Array.from({ length: data.length }, () => this.primaryColorValue)

    this.chart = new Chart(ctx, {
      type: 'bar',
      plugins: [ChartDataLabels],
      data: {
        labels: this.labelsValue,
        datasets: [{
          data: data,
          backgroundColor: barColors,
          borderWidth: 0
        }]
      },
      options: {
        indexAxis: horizontal ? 'y' : 'x',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
          datalabels: {
            // Near-full-length bars would push an end-anchored label past the
            // chart's edge and clip it -- flip the label inside the bar (and
            // to a legible colour) once the bar is long enough to have room.
            color: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return maxValue > 0 && (value / maxValue) > 0.85 ? readableTextColor(barColors[context.dataIndex]) : labelColor
            },
            anchor: 'end',
            align: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return maxValue > 0 && (value / maxValue) > 0.85 ? 'start' : 'end'
            },
            clamp: true,
            font: { weight: labelFontWeight(this.labelWeightValue), size: labelFontSize(this.labelSizeValue) },
            formatter: (value, context) => {
              if (value <= 0) return ''
              const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0)
              const chartOwnPct = total > 0 ? Math.round(value / total * 100) + '%' : ''
              if (labelStyle === 'none') return ''
              if (labelStyle === 'count') return progress ? `${value} of ${denominator}` : value
              if (labelStyle === 'both') return `${value} (${resolvedSegmentPercentage(value, total, denominator)}%)`
              if (labelStyle === 'percentage_of_responses') {
                return `${resolvedSegmentPercentage(value, total, denominator)}%`
              }
              if (labelStyle === 'percentage') return chartOwnPct
              return value
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { display: false },
            ticks: { precision: 0, display: !horizontal },
            border: { display: !horizontal }
          },
          y: {
            beginAtZero: true,
            grid: { display: false },
            // Horizontal mode (indexAxis: 'y') puts the category labels on
            // this axis -- wrap/truncate those (card #4273). Vertical mode
            // uses this axis for numeric values, so the callback is left off
            // entirely rather than risk mangling a number.
            ticks: horizontal
              ? { precision: 0, callback: (value, index) => wrapLabel(labels[index]) }
              : { precision: 0 }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600 }
      }
    })
  }
}
